import { Button } from '@/lib';
import { BaseModal } from '@/lib/Modals';
import { cn } from '@/lib/utils';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconType } from 'react-icons';
import { LuCheck, LuFilePlus, LuRefreshCw } from 'react-icons/lu';

export type ImportMode = 'new' | 'update';

const IMPORT_MODES: { id: ImportMode; labelKey: string; hintKey: string; Icon: IconType }[] = [
  { id: 'new', labelKey: 'import.modeNew', hintKey: 'import.modeNewHint', Icon: LuFilePlus },
  { id: 'update', labelKey: 'import.modeUpdate', hintKey: 'import.modeUpdateHint', Icon: LuRefreshCw },
];

type ImportModeDialogProps = {
  onClose: () => void;
  onSelect: (mode: ImportMode) => void;
};

/** Two-option chooser shown before the import flow: create a new list, or resync an existing one. */
export const ImportModeDialog: FC<ImportModeDialogProps> = ({ onClose, onSelect }) => {
  const { t } = useTranslation('comuni');
  const [selected, setSelected] = useState<ImportMode | null>(null);

  return (
    <BaseModal onClose={onClose} classNames={{ dialog: 'max-w-2xl w-full', content: 'min-h-0 p-0' }}>
      <div className='p-6 sm:p-8'>
        <h2 className='text-[22px] font-semibold tracking-tight text-primary-900'>{t('import.modeTitle')}</h2>
        <p className='mt-1.5 text-[14.5px] text-tertiary-600'>{t('import.modeSubtitle')}</p>

        <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2'>
          {IMPORT_MODES.map(({ id, labelKey, hintKey, Icon }) => {
            const isSelected = selected === id;
            return (
              <button
                key={id}
                type='button'
                aria-pressed={isSelected}
                onClick={() => setSelected(id)}
                className={cn(
                  'relative flex flex-col gap-3.5 rounded-[14px] border p-5 text-left',
                  isSelected ? 'border-2 border-brand-600 bg-brand-50 p-[19px]' : 'bg-white-100 hover:border-brand-200'
                )}
              >
                {isSelected && (
                  <span className='absolute right-3 top-3 flex size-[22px] items-center justify-center rounded-full bg-brand-700'>
                    <LuCheck className='size-3 text-white-100' strokeWidth={3} />
                  </span>
                )}
                <span className='flex size-11 items-center justify-center rounded-xl bg-brand-50'>
                  <Icon className='size-6 text-brand-700' />
                </span>
                <span className='text-[15px] font-semibold leading-snug text-primary-900'>{t(labelKey)}</span>
                <span className='text-sm leading-relaxed text-tertiary-600'>{t(hintKey)}</span>
              </button>
            );
          })}
        </div>

        <div className='mt-6 flex justify-end gap-3'>
          <Button intent='secondary' size='md' onClick={onClose} text={t('composer.saveCancel')} />
          <Button intent='primary' size='md' disabled={!selected} onClick={() => selected && onSelect(selected)} text={t('modelli.editor.continue')} />
        </div>
      </div>
    </BaseModal>
  );
};
